import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ChatFab, ChatPopup } from "@/components/ChatPopup";
import { Megaphone } from "@phosphor-icons/react";
import { ShareLandingMenu } from "@/components/ShareLandingMenu";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { GlobalSearch } from "@/components/GlobalSearch";
import { OfflineBanner } from "@/components/OfflineBanner";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Gauge, Storefront, Package, FilePlus, Archive, GearSix, SignOut, DotsThree, Briefcase,
  ChartPieSlice, Receipt, UsersThree, ListChecks, CalendarCheck, Wrench, Gift, Certificate, SimCard,
  Headset, ClipboardText, Broadcast, CaretDown, ChartLineUp, Clock, CalendarBlank, CalendarDots, Coins, FileText, List, ChatsCircle,
} from "@phosphor-icons/react";

const PRIMARY = [
  { to: "/", label: "Dashboard", tid: "dashboard", icon: Gauge, end: true },
  { to: "/pipeline", label: "Pipeline", tid: "pipeline", icon: ChartLineUp },
  { to: "/social-calendar", label: "Social Calendar", tid: "social-calendar", icon: CalendarBlank },
  { to: "/quote/new", label: "Estimator", tid: "estimator", icon: FilePlus },
  { to: "/quotes", label: "Saved Quotes", tid: "saved-quotes", icon: Archive },
  { to: "/job-completion", label: "Job Completion", tid: "job-completion", icon: Briefcase },
  { to: "/work-orders", label: "Work Orders", tid: "work-orders", icon: Wrench },
  { to: "/day-schedule", label: "Day Schedule", tid: "day-schedule", icon: CalendarDots },
  { to: "/services", label: "Service Schedule", tid: "services", icon: CalendarCheck },
  { to: "/invoices", label: "Invoicing", tid: "invoices", icon: Receipt },
  { to: "/hub-invoices", label: "Hub Invoice", tid: "hub-invoices", icon: FileText },
  { to: "/customers", label: "Customers", tid: "customers", icon: UsersThree },
  { to: "/whatsapp", label: "Campaigns", tid: "whatsapp", icon: Megaphone },
  { to: "/dispatch", label: "Support & Dispatch", tid: "dispatch", icon: Headset },
];
const MORE = [
  { to: "/profit-loss", label: "P&L Overview", tid: "profit-loss", icon: Coins },
  { to: "/jobs", label: "Jobs (tracking)", tid: "jobs", icon: Briefcase },
  { to: "/my-schedule", label: "My Schedule", tid: "my-schedule", icon: ClipboardText },
  { to: "/my-jobs", label: "Today's Jobs (mobile)", tid: "my-jobs", icon: Clock },
  { to: "/cost-variance", label: "Cost Variance", tid: "cost-variance", icon: ChartLineUp },
  { to: "/suppliers", label: "Suppliers", tid: "suppliers", icon: Storefront },
  { to: "/parts", label: "Parts Catalog", tid: "parts", icon: Package },
  { to: "/features", label: "Feature Library", tid: "features", icon: ListChecks },
  { to: "/addons", label: "Add-ons", tid: "addons", icon: Gift },
  { to: "/sim-topups", label: "SIM Top-ups", tid: "sim-topups", icon: SimCard },
  { to: "/monitoring", label: "Monitoring", tid: "monitoring", icon: Broadcast },
  { to: "/certificates", label: "Certificates", tid: "certificates", icon: Certificate },
  { to: "/contracts", label: "Contracts", tid: "contracts", icon: ChartPieSlice },
  { to: "/settings", label: "Settings", tid: "settings", icon: GearSix },
];
const ALL = [...PRIMARY, ...MORE];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState({});
  const [overdue, setOverdue] = useState(0);
  const [unread, setUnread] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const location = useLocation();
  const openChat = () => { setUnread(false); setChatOpen(true); };
  useEffect(() => { setChatOpen(false); }, [location.pathname]);

  // Team Messages badge: newest message from someone else after what this device last read
  useEffect(() => {
    const check = () => api.get("/team-messages", { params: { limit: 1 } }).then((r) => {
      const m = r.data?.[0];
      setUnread(!!m && m.user_id !== user?.id && m.created_at > (localStorage.getItem("tm_last_read") || "") && window.location.pathname !== "/messages");
    }).catch(() => {});
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [user?.id]);

  useEffect(() => { api.get("/business").then((r) => setBusiness(r.data)).catch(() => {}); }, []);

  // Count of "Earlier — not done" jobs (past, non-completed) for this phone's engineer, for the nav badge.
  useEffect(() => {
    const cyprusToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Nicosia" });
    const load = () => api.get("/work-orders").then((r) => {
      const t = cyprusToday();
      const myEng = localStorage.getItem("tk_my_engineer") || "";
      const wos = (r.data || []).filter((w) => (myEng ? w.engineer_id === myEng : true));
      setOverdue(wos.filter((w) => w.scheduled_date && w.scheduled_date < t && w.status !== "completed").length);
    }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  const iconLinkClass = ({ isActive }) =>
    `flex items-center justify-center w-11 h-11 transition-colors ${
      isActive ? "bg-brand-gold text-brand-navy" : "text-slate-300 hover:bg-white/10 hover:text-white"
    }`;

  return (
    <TooltipProvider delayDuration={200}>
      <OfflineBanner />
      <div className="flex flex-col h-screen overflow-hidden">
        <header className="bg-brand-navy text-white border-b border-black/20 shrink-0 z-40">
          <div className="h-16 px-3 md:px-5 flex items-center gap-3">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
              <div className="w-9 h-9 bg-white flex items-center justify-center overflow-hidden">
                <img src={business.logo_url || "/armstrong-logo.png"} alt="Armstrong logo" className="w-full h-full object-contain" data-testid="nav-logo-img"
                  onError={(e) => { if (!e.currentTarget.src.endsWith("/armstrong-logo.png")) e.currentTarget.src = "/armstrong-logo.png"; }} />
              </div>
              <div className="leading-tight hidden sm:block">
                <div className="font-heading font-bold text-sm tracking-tight">ArmstrongOne</div>
                <div className="text-[9px] uppercase tracking-[0.25em] text-brand-gold">Business Hub</div>
              </div>
            </NavLink>

            {/* Global search (center) */}
            <div className="flex-1 flex justify-center px-1 md:px-2"><GlobalSearch /></div>

            <ShareLandingMenu business={business} />

            <button type="button" onClick={openChat} className={`relative ${iconLinkClass({ isActive: chatOpen })}`} title="Team Messages" data-testid="nav-messages">
              <ChatsCircle size={22} weight="duotone" />
              {unread && <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-brand-navy" data-testid="nav-messages-unread" />}
            </button>

            {/* Primary icon row (desktop) */}
            <nav className="hidden lg:flex items-center gap-0.5 shrink-0" data-testid="top-nav">
              {PRIMARY.map((n) => (
                <Tooltip key={n.to}>
                  <TooltipTrigger asChild>
                    <NavLink to={n.to} end={n.end} className={iconLinkClass} data-testid={`nav-${n.tid}`}>
                      <n.icon size={20} weight="duotone" />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent>{n.label}</TooltipContent>
                </Tooltip>
              ))}
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center justify-center w-11 h-11 text-slate-300 hover:bg-white/10 hover:text-white transition-colors" data-testid="nav-more">
                        <DotsThree size={24} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>More</TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" className="w-56">
                  {MORE.map((n) => (
                    <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={`nav-${n.tid}`}>
                      <n.icon size={18} weight="duotone" className="mr-2" /> {n.label}
                      {n.tid === "my-jobs" && overdue > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none">{overdue > 99 ? "99+" : overdue}</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Account + mobile nav */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative flex items-center gap-1.5 h-10 max-sm:min-h-11 px-1.5 text-slate-200 hover:text-white shrink-0" data-testid="account-menu">
                  <List size={22} weight="bold" className="lg:hidden" data-testid="mobile-menu-icon" />
                  <div className="w-8 h-8 rounded-full bg-brand-gold text-brand-navy flex items-center justify-center font-bold text-sm">
                    {(user?.email || "?")[0].toUpperCase()}
                  </div>
                  {overdue > 0 && (
                    <span className="absolute -top-0.5 left-5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none ring-2 ring-brand-navy" data-testid="nav-overdue-badge">
                      {overdue > 99 ? "99+" : overdue}
                    </span>
                  )}
                  <CaretDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user?.email}</div>
                <div className="lg:hidden max-h-72 overflow-y-auto border-t border-border mt-1 pt-1">
                  {ALL.map((n) => (
                    <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={`m-nav-${n.tid}`}>
                      <n.icon size={18} weight="duotone" className="mr-2" /> {n.label}
                      {n.tid === "my-jobs" && overdue > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none">{overdue > 99 ? "99+" : overdue}</span>}
                    </DropdownMenuItem>
                  ))}
                </div>
                <DropdownMenuItem onClick={() => navigate("/timekeeping")} data-testid="m-nav-timekeeping" className="border-t border-border mt-1">
                  <Clock size={18} weight="duotone" className="mr-2" /> Time Keeping
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-destructive border-t border-border mt-1">
                  <SignOut size={18} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-background pb-20">{children}</main>
      </div>
      {location.pathname !== "/messages" && (chatOpen ? <ChatPopup onClose={() => setChatOpen(false)} /> : <ChatFab unread={unread} onOpen={openChat} />)}
    </TooltipProvider>
  );
}
